<template>
  <div class="banner-list-container">
    <h2>轮播图管理</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <div class="filter-group">
          <el-input
            v-model="searchQuery"
            placeholder="搜索标题或描述"
            style="width: 300px; margin-right: 10px;"
            clearable
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><search /></el-icon>
            </template>
          </el-input>
          <el-select
            v-model="statusFilter"
            placeholder="筛选状态"
            style="width: 150px; margin-right: 10px;"
            clearable
          >
            <el-option label="全部" value="" />
            <el-option label="启用" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
        </div>
        <el-button type="primary" @click="handleCreate">
          <el-icon><plus /></el-icon> 添加轮播图
        </el-button>
      </div>
      
      <el-table :data="banners" border stripe style="width: 100%" row-key="id" v-loading="loading">
        <!-- <el-table-column prop="id" label="ID" width="80" /> -->
        <el-table-column prop="title" label="标题" />
        <el-table-column label="图片" width="200">
          <template #default="scope">
            <el-image
              :src="getFullImageUrl(scope.row.image_url)"
              :fit="'cover'"
              style="width: 150px; height: 80px;"
            />
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="150">
          <template #default="scope">
            <div class="status-container">
              <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'">
                {{ scope.row.status === 'active' ? '启用' : '禁用' }}
              </el-tag>
              <el-switch
                v-model="scope.row.status"
                active-value="active"
                inactive-value="inactive"
                @change="handleStatusChange(scope.row)"
                style="margin-left: 10px;"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="排序" width="150">
          <template #default="scope">
            <el-input-number 
              v-model="scope.row.sort_order" 
              :min="0" 
              :precision="0"
              :step="1"
              @change="handleSortChange(scope.row)"
              style="width: 100%"
              placeholder="请输入"
            />
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template #default="scope">
            <el-tag :type="getModeType(scope.row.mode)">
              {{ getModeLabel(scope.row.mode) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- <el-table-column prop="created_at" label="创建时间" width="180" /> -->
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">
              <el-icon><edit /></el-icon> 编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">
              <el-icon><delete /></el-icon> 删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 添加轮播图抽屉 -->
    <el-drawer
      v-model="addDialogVisible"
      title="添加轮播图"
      direction="rtl"
      size="50%"
      @close="resetAddForm"
    >
      <el-form :model="addForm" :rules="addRules" ref="addFormRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="标题" prop="title">
              <el-input v-model="addForm.title" placeholder="请输入标题" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="轮播图片" prop="image_url">
              <!-- 图片大小提示 -->
              <el-alert
                title="图片上传提示"
                type="info"
                :closable="false"
                show-icon
                style="margin-bottom: 16px;"
              >
                <template #default>
                  <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
                </template>
              </el-alert>
              
              <el-upload
                :file-list="imageFileList"
                :action="`${apiBaseUrl}/manage/upload/image`"
                list-type="picture-card"
                :auto-upload="true"
                :on-preview="handlePictureCardPreview"
                :on-remove="handleRemove"
                :on-success="handleImageUploadSuccess"
                :on-error="handleImageUploadError"
                :before-upload="beforeImageUpload"
                :data="{ type: 'content' }"
                :headers="{ 'Authorization': `Bearer ${authToken}` }"
              >
                <el-icon><plus /></el-icon>
                <template #tip>
                  <div class="el-upload__tip">
                    只能上传jpg/png文件，且不超过20MB
                  </div>
                </template>
              </el-upload>
              <el-dialog v-model="previewVisible" title="预览图片">
                <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
              </el-dialog>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="排序" prop="sort_order">
              <el-input v-model="addForm.sort_order" type="number" placeholder="请输入排序号" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="模式" prop="mode">
              <el-radio-group v-model="addForm.mode">
                <el-radio label="display">纯展示</el-radio>
                <el-radio label="link">链接</el-radio>
                <el-radio label="exchange_product">兑换商品</el-radio>
                <el-radio label="appointment_product">预约商品</el-radio>
                <el-radio label="rich">富文本</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="addForm.status">
                <el-radio label="active">启用</el-radio>
                <el-radio label="inactive">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 链接模式下显示链接地址 -->
        <el-row :gutter="20" v-if="addForm.mode === 'link'">
          <el-col :span="24">
            <el-form-item label="链接地址" prop="link_url">
              <el-input v-model="addForm.link_url" placeholder="请输入链接地址" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 兑换商品模式下显示兑换商品选择 -->
        <el-row :gutter="20" v-if="addForm.mode === 'exchange_product'">
          <el-col :span="24">
            <el-form-item label="选择兑换商品" prop="product_id">
              <el-select v-model="addForm.product_id" placeholder="请选择兑换商品" style="width: 100%" :loading="exchangeProductsLoading">
                <el-option
                  v-for="product in exchangeProducts"
                  :key="product.id"
                  :label="product.name"
                  :value="product.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 预约商品模式下显示预约商品选择 -->
        <el-row :gutter="20" v-if="addForm.mode === 'appointment_product'">
          <el-col :span="24">
            <el-form-item label="选择预约商品" prop="product_id">
              <el-select v-model="addForm.product_id" placeholder="请选择预约商品" style="width: 100%" :loading="appointmentProductsLoading">
                <el-option
                  v-for="product in appointmentProducts"
                  :key="product.id"
                  :label="product.title"
                  :value="product.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 富文本模式下显示富文本编辑器 -->
        <el-row :gutter="20" v-if="addForm.mode === 'rich'">
          <el-col :span="24">
            <el-form-item label="富文本内容" prop="content">
              <div class="editor-container">
                <Toolbar
                  style="border-bottom: 1px solid #ccc"
                  :editor="editorInstance"
                  :default-config="editorConfig"
                  :mode="'default'"
                />
                <Editor
                  v-model="addForm.content"
                  ref="editorRef"
                  style="height: 300px; overflow-y: auto"
                  :default-config="editorConfig"
                  :mode="'default'"
                  @onCreated="handleCreated"
                  @onChange="handleChange"
                />
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        

      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitAddForm">确定</el-button>
        </div>
      </template>
    </el-drawer>
    
    <!-- 编辑轮播图抽屉 -->
    <el-drawer
      v-model="editDialogVisible"
      title="编辑轮播图"
      direction="rtl"
      size="50%"
      @close="resetEditForm"
    >
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="标题" prop="title">
              <el-input v-model="editForm.title" placeholder="请输入标题" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="轮播图片" prop="image_url">
              <!-- 图片大小提示 -->
              <el-alert
                title="图片上传提示"
                type="info"
                :closable="false"
                show-icon
                style="margin-bottom: 16px;"
              >
                <template #default>
                  <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
                </template>
              </el-alert>
              
              <el-upload
                :file-list="editImageFileList"
                :action="`${apiBaseUrl}/manage/upload/image`"
                list-type="picture-card"
                :auto-upload="true"
                :on-preview="handleEditPictureCardPreview"
                :on-remove="handleEditRemove"
                :on-success="handleEditImageUploadSuccess"
                :on-error="handleEditImageUploadError"
                :before-upload="beforeImageUpload"
                :data="{ type: 'content' }"
                :headers="{ 'Authorization': `Bearer ${authToken}` }"
              >
                <el-icon><plus /></el-icon>
                <template #tip>
                  <div class="el-upload__tip">
                    只能上传jpg/png文件，且不超过20MB
                  </div>
                </template>
              </el-upload>
              <el-dialog v-model="previewVisible" title="预览图片">
                <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
              </el-dialog>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="排序" prop="sort_order">
              <el-input v-model="editForm.sort_order" type="number" placeholder="请输入排序号" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="模式" prop="mode">
              <el-radio-group v-model="editForm.mode">
                <el-radio label="display">纯展示</el-radio>
                <el-radio label="link">链接</el-radio>
                <el-radio label="exchange_product">兑换商品</el-radio>
                <el-radio label="appointment_product">预约商品</el-radio>
                <el-radio label="rich">富文本</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="editForm.status">
                <el-radio label="active">启用</el-radio>
                <el-radio label="inactive">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 链接模式下显示链接地址 -->
        <el-row :gutter="20" v-if="editForm.mode === 'link'">
          <el-col :span="24">
            <el-form-item label="链接地址" prop="link_url">
              <el-input v-model="editForm.link_url" placeholder="请输入链接地址" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 兑换商品模式下显示兑换商品选择 -->
        <el-row :gutter="20" v-if="editForm.mode === 'exchange_product'">
          <el-col :span="24">
            <el-form-item label="选择兑换商品" prop="product_id">
              <el-select v-model="editForm.product_id" placeholder="请选择兑换商品" style="width: 100%" :loading="exchangeProductsLoading">
                <el-option
                  v-for="product in exchangeProducts"
                  :key="product.id"
                  :label="product.name"
                  :value="product.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 预约商品模式下显示预约商品选择 -->
        <el-row :gutter="20" v-if="editForm.mode === 'appointment_product'">
          <el-col :span="24">
            <el-form-item label="选择预约商品" prop="product_id">
              <el-select v-model="editForm.product_id" placeholder="请选择预约商品" style="width: 100%" :loading="appointmentProductsLoading">
                <el-option
                  v-for="product in appointmentProducts"
                  :key="product.id"
                  :label="product.name"
                  :value="product.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 富文本模式下显示富文本编辑器 -->
        <el-row :gutter="20" v-if="editForm.mode === 'rich'">
          <el-col :span="24">
            <el-form-item label="富文本内容" prop="content">
              <div class="editor-container">
                <Toolbar
                  style="border-bottom: 1px solid #ccc"
                  :editor="editEditorInstance"
                  :default-config="editEditorConfig"
                  :mode="'default'"
                />
                <Editor
                  v-model="editForm.content"
                  ref="editEditorRef"
                  style="height: 300px; overflow-y: auto"
                  :default-config="editEditorConfig"
                  :mode="'default'"
                  @onCreated="handleEditCreated"
                  @onChange="handleEditChange"
                />
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        

      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitEditForm">确定</el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Edit, Delete } from '@element-plus/icons-vue';
import request from '../../api/axios';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
// 导入编辑器样式
import '@wangeditor/editor/dist/css/style.css';

export default {
  name: 'BannerList',
  components: {
    Search,
    Plus,
    Edit,
    Delete,
    Editor,
    Toolbar
  },
  setup() {
    // 获取API基础URL，与axios配置保持一致
    const apiBaseUrl = ref(process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api');
    
    // 获取认证token
    const getToken = () => {
      try {
        return localStorage.getItem('token') || '';
      } catch (e) {
        console.error('Failed to access localStorage:', e);
        return '';
      }
    };
    
    // 认证token
    const authToken = ref(getToken());
    
    // 基本状态变量
    const banners = ref([]);
    const searchQuery = ref('');
    const statusFilter = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    // 兑换商品列表
    const exchangeProducts = ref([]);
    const exchangeProductsLoading = ref(false);
    // 预约商品列表
    const appointmentProducts = ref([]);
    const appointmentProductsLoading = ref(false);
    
    // 添加轮播图相关状态
    const addDialogVisible = ref(false);
    const addFormRef = ref(null);
    const addForm = ref({
      title: '',
      image_url: '',
      status: 'active',
      sort_order: 0,
      mode: 'display',
      content: '',
      link_url: '',
      product_id: ''
    });
    
    // 编辑轮播图相关状态
    const editDialogVisible = ref(false);
    const editFormRef = ref(null);
    const editForm = ref({
      title: '',
      image_url: '',
      status: 'active',
      sort_order: 0,
      mode: 'display',
      content: '',
      link_url: '',
      product_id: ''
    });
    
    // 编辑器相关状态
    const editorRef = ref(null);
    const editEditorRef = ref(null);
    const editorInstance = ref(null);
    const editEditorInstance = ref(null);
    
    // 编辑器配置
    const editorConfig = ref({
      placeholder: '请输入内容...',
      minHeight: 300,
      MENU_CONF: {
        uploadImage: {
          // 上传图片的接口地址
          server: `${apiBaseUrl}/manage/upload/image`,
          // 上传字段名
          fieldName: 'file',
          // 图片大小限制，单位KB
          maxFileSize: 20480, // 20MB
          // 自定义上传参数
          meta: {
            type: 'banner' // 指定上传类型为banner
          },
          // 自定义请求头
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Authorization': `Bearer ${authToken.value}` // 添加认证token
          },
          // 自定义上传图片，完全控制上传过程
          customUpload(file, insertFn) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'banner');
            
            // 直接使用axios而不是全局request，避免响应拦截器影响
            import('axios').then(axios => {
              axios.default({
                url: `${apiBaseUrl}/manage/upload/image`,
                method: 'post',
                data: formData,
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': `Bearer ${authToken.value}`
                },
                timeout: 15000
              }).then(response => {
                const res = response.data;
                // 处理上传成功的响应
                if (res.errno === 0 && res.data && res.data.length > 0) {
                  let imgUrl = res.data[0].url;
                  // 确保返回的是完整的图片URL
                  if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
                    imgUrl = `${window.location.origin}${imgUrl}`;
                  }
                  // 使用insertFn插入图片
                  insertFn(imgUrl, res.data[0].alt || file.name, imgUrl);
                } else {
                  ElMessage.error('图片上传失败: ' + (res.message || '未知错误'));
                }
              }).catch(error => {
                console.error('上传失败', error);
                ElMessage.error('图片上传失败: ' + (error.message || '网络错误'));
              });
            });
          },
          // 上传失败后的回调
          onFailed(file, res) {
            console.error('上传失败', res);
            let errorMessage = '未知错误';
            if (res) {
              if (res.message) {
                errorMessage = res.message;
              } else if (res.data && res.data.message) {
                errorMessage = res.data.message;
              } else if (res.errno !== undefined && res.errno !== 0) {
                errorMessage = res.data || '上传失败';
              }
            }
            ElMessage.error('图片上传失败: ' + errorMessage);
          }
        }
      }
    });
    
    const editEditorConfig = ref({
      placeholder: '请输入内容...',
      minHeight: 300,
      MENU_CONF: {
        uploadImage: {
          // 上传图片的接口地址
          server: `${apiBaseUrl}/manage/upload/image`,
          // 上传字段名
          fieldName: 'file',
          // 图片大小限制，单位KB
          maxFileSize: 20480, // 20MB
          // 自定义上传参数
          meta: {
            type: 'banner' // 指定上传类型为banner
          },
          // 自定义请求头
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Authorization': `Bearer ${authToken.value}` // 添加认证token
          },
          // 自定义上传图片，完全控制上传过程
          customUpload(file, insertFn) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'banner');
            
            // 直接使用axios而不是全局request，避免响应拦截器影响
            import('axios').then(axios => {
              axios.default({
                url: `${apiBaseUrl}/manage/upload/image`,
                method: 'post',
                data: formData,
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': `Bearer ${authToken.value}`
                },
                timeout: 15000
              }).then(response => {
                const res = response.data;
                // 处理上传成功的响应
                if (res.errno === 0 && res.data && res.data.length > 0) {
                  let imgUrl = res.data[0].url;
                  // 确保返回的是完整的图片URL
                  if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
                    imgUrl = `${window.location.origin}${imgUrl}`;
                  }
                  // 使用insertFn插入图片
                  insertFn(imgUrl, res.data[0].alt || file.name, imgUrl);
                } else {
                  ElMessage.error('图片上传失败: ' + (res.message || '未知错误'));
                }
              }).catch(error => {
                console.error('上传失败', error);
                ElMessage.error('图片上传失败: ' + (error.message || '网络错误'));
              });
            });
          },
          // 上传失败后的回调
          onFailed(file, res) {
            console.error('上传失败', res);
            let errorMessage = '未知错误';
            if (res) {
              if (res.message) {
                errorMessage = res.message;
              } else if (res.data && res.data.message) {
                errorMessage = res.data.message;
              } else if (res.errno !== undefined && res.errno !== 0) {
                errorMessage = res.data || '上传失败';
              }
            }
            ElMessage.error('图片上传失败: ' + errorMessage);
          }
        }
      }
    });
    
    // 编辑器初始化完成时的回调
    const handleCreated = (editor) => {
      editorInstance.value = editor;
      console.log('添加编辑器初始化完成');
      // 设置初始内容
      editor.setHtml(addForm.value.content || '<p></p>');
    };
    
    // 编辑器内容变化时的回调
    const handleChange = (editor) => {
      addForm.value.content = editor.getHtml();
    };
    
    // 编辑模式编辑器初始化完成时的回调
    const handleEditCreated = (editor) => {
      editEditorInstance.value = editor;
      console.log('编辑编辑器初始化完成');
      // 设置初始内容
      editor.setHtml(editForm.value.content || '<p></p>');
    };
    
    // 编辑模式编辑器内容变化时的回调
    const handleEditChange = (editor) => {
      editForm.value.content = editor.getHtml();
    };
    
    // 组件销毁前销毁编辑器
    onBeforeUnmount(() => {
      // 从编辑器实例销毁
      if (editorInstance.value) {
        editorInstance.value.destroy();
        editorInstance.value = null;
      }
      if (editEditorInstance.value) {
        editEditorInstance.value.destroy();
        editEditorInstance.value = null;
      }
    });
    
    // 监听addDialogVisible变化，当抽屉打开时初始化编辑器
    watch(
      () => addDialogVisible.value,
      (newVal) => {
        if (newVal) {
          // 打开抽屉时不需要特殊处理，编辑器会在onCreated中初始化
        }
      }
    );
    
    // 监听editDialogVisible变化，当抽屉打开时初始化编辑器
    watch(
      () => editDialogVisible.value,
      (newVal) => {
        if (newVal) {
          // 打开抽屉时不需要特殊处理，编辑器会在onCreated中初始化
        }
      }
    );
    
    // 监听添加表单模式变化，确保编辑器能正确初始化
    watch(
      () => addForm.value.mode,
      (newMode) => {
        console.log('添加表单模式变化:', newMode);
        // 当模式切换到富文本时，确保编辑器实例已创建
        if (newMode === 'rich' && editorInstance.value) {
          console.log('富文本编辑器实例存在');
        } else if (newMode === 'rich') {
          console.log('富文本编辑器实例不存在，将在渲染后创建');
        }
      }
    );
    
    // 监听编辑表单模式变化，确保编辑器能正确初始化
    watch(
      () => editForm.value.mode,
      (newMode) => {
        console.log('编辑表单模式变化:', newMode);
        // 当模式切换到富文本时，确保编辑器实例已创建
        if (newMode === 'rich' && editEditorInstance.value) {
          console.log('编辑富文本编辑器实例存在');
        } else if (newMode === 'rich') {
          console.log('编辑富文本编辑器实例不存在，将在渲染后创建');
        }
      }
    );
    
    // 图片上传相关变量
    const imageFileList = ref([]);
    const editImageFileList = ref([]);
    const previewVisible = ref(false);
    const dialogImageUrl = ref('');
    
    // 获取兑换商品列表
    const fetchExchangeProducts = async () => {
      exchangeProductsLoading.value = true;
      try {
        const response = await request({
          url: '/manage/products',
          method: 'get',
          params: {
            pageSize: 999, // 获取所有商品
            status: 'active', // 只获取激活状态的商品
            // product_type: 'exchange' // 指定为兑换商品
          }
        });
        // 处理API返回的数据结构，确保是正确的数组格式
        exchangeProducts.value = response.data.data || response.data || [];
        console.log('兑换商品列表:', exchangeProducts.value);
      } catch (error) {
        ElMessage.error('获取兑换商品列表失败');
        console.error('获取兑换商品列表错误:', error);
        exchangeProducts.value = [];
      } finally {
        exchangeProductsLoading.value = false;
      }
    };
    
    // 获取预约商品列表
    const fetchAppointmentProducts = async () => {
      appointmentProductsLoading.value = true;
      try {
        const response = await request({
          url: '/manage/appointment-products',
          method: 'get',
          params: {
            pageSize: 999, // 获取所有商品
            status: 'active' // 只获取激活状态的商品
          }
        });
        // 处理API返回的数据结构，确保是正确的数组格式
        appointmentProducts.value = response.data.data?.items || response.data?.items || [];
        console.log('预约商品列表:', appointmentProducts.value);
      } catch (error) {
        ElMessage.error('获取预约商品列表失败');
        console.error('获取预约商品列表错误:', error);
        appointmentProducts.value = [];
      } finally {
        appointmentProductsLoading.value = false;
      }
    };
    
    const addRules = {
      title: [
        { required: true, message: '请输入标题', trigger: 'blur' }
      ],
      image_url: [
        { required: true, message: '请上传轮播图片', trigger: 'change' }
      ],
      mode: [
        { required: true, message: '请选择轮播图模式', trigger: 'change' }
      ],
      link_url: [
        { required: true, message: '请输入链接地址', trigger: 'blur', when: (form) => form.mode === 'link' },
        { type: 'url', message: '请输入有效的URL地址', trigger: 'blur', when: (form) => form.mode === 'link' }
      ],
      product_id: [
        { required: true, message: '请选择商品', trigger: 'change' }
      ],
      content: [
        { required: true, message: '请输入富文本内容', trigger: 'blur', when: (form) => form.mode === 'rich' }
      ]
    };
    
    const editRules = {
      title: [
        { required: true, message: '请输入标题', trigger: 'blur' }
      ],
      image_url: [
        { required: true, message: '请上传轮播图片', trigger: 'change' }
      ],
      mode: [
        { required: true, message: '请选择轮播图模式', trigger: 'change' }
      ],
      link_url: [
        { required: true, message: '请输入链接地址', trigger: 'blur', when: (form) => form.mode === 'link' },
        { type: 'url', message: '请输入有效的URL地址', trigger: 'blur', when: (form) => form.mode === 'link' }
      ],
      product_id: [
        { required: true, message: '请选择商品', trigger: 'change' }
      ],
      content: [
        { required: true, message: '请输入富文本内容', trigger: 'blur', when: (form) => form.mode === 'rich' }
      ]
    };
    
    // 获取轮播图列表
    const fetchBanners = async () => {
      loading.value = true;
      try {
        const response = await request({
          url: '/manage/banners',
          method: 'get',
          params: {
            page: currentPage.value,
            pageSize: pageSize.value,
            search: searchQuery.value,
            status: statusFilter.value
          }
        });
        // 确保banners始终是数组，防止表格expand功能出错
        let bannersData = [];
        if (response && response.code === 200) {
          // 正确处理分页数据结构
          const paginatedData = response.data;
          bannersData = paginatedData.data || [];
          total.value = paginatedData.total || 0;
        } else if (response && Array.isArray(response.data)) {
          bannersData = response.data;
          total.value = bannersData.length;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          // 处理嵌套数组结构
          bannersData = response.data.data;
          total.value = response.data.total || 0;
        }
        banners.value = bannersData;
      } catch (error) {
        ElMessage.error('获取轮播图列表失败');
        console.error(error);
        // 出错时重置为数组
        banners.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };
    
    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1;
      fetchBanners();
    };
    
    // 页面加载时获取轮播图列表和商品列表
    onMounted(() => {
      fetchBanners();
      fetchExchangeProducts();
      fetchAppointmentProducts();
    });
    
    // 打开添加轮播图抽屉
    const handleCreate = () => {
      addDialogVisible.value = true;
    };
    
    // 重置添加轮播图表单
    const resetAddForm = () => {
      addForm.value = {
        title: '',
        image_url: '',
        status: 'active',
        sort_order: 0,
        mode: 'display',
        content: '',
        link_url: '',
        product_id: ''
      };
      imageFileList.value = [];
      if (addFormRef.value) {
        addFormRef.value.resetFields();
      }
      // 重置编辑器实例
      if (editorInstance.value) {
        editorInstance.value.setHtml('');
      }
    };
    
    // 重置编辑轮播图表单
    const resetEditForm = () => {
      editForm.value = {
        title: '',
        image_url: '',
        status: 'active',
        sort_order: 0,
        mode: 'display',
        content: '',
        link_url: '',
        product_id: ''
      };
      editImageFileList.value = [];
      if (editFormRef.value) {
        editFormRef.value.resetFields();
      }
      // 重置编辑器实例
      if (editEditorInstance.value) {
        editEditorInstance.value.setHtml('');
      }
    };
    
    // 监听addDialogVisible变化，初始化图片文件列表
    watch(
      () => addDialogVisible.value,
      (newVal) => {
        if (newVal) {
          // 初始化图片文件列表
          imageFileList.value = [];
        }
      }
    );
    
    // 监听editDialogVisible变化，初始化图片文件列表
    watch(
      () => editDialogVisible.value,
      (newVal) => {
        if (newVal) {
          // 初始化图片文件列表，带当前图片
          if (editForm.value.image_url) {
            let previewUrl = editForm.value.image_url;
            // 预览需要完整URL，使用API域名
            if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
              // 从API基础URL中提取域名
              const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
              const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
              const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
              previewUrl = `${apiDomain}${previewUrl}`;
            }
            editImageFileList.value = [{
              url: previewUrl,
              name: '轮播图',
              status: 'success'
            }];
          } else {
            editImageFileList.value = [];
          }
        }
      }
    );
    
    // 监听添加表单模式变化
    watch(
      () => addForm.value.mode,
      (newMode) => {
        // 清空其他模式相关字段
        if (newMode !== 'link') {
          addForm.value.link_url = '';
        }
        if (newMode !== 'rich') {
          addForm.value.content = '';
          // 重置编辑器内容
          if (editorInstance.value) {
            editorInstance.value.setHtml('');
          }
        }
        if (newMode !== 'exchange_product' && newMode !== 'appointment_product') {
          addForm.value.product_id = '';
        }
        // 重置表单验证状态
        if (addFormRef.value) {
          addFormRef.value.clearValidate();
        }
      }
    );
    
    // 监听编辑表单模式变化
    watch(
      () => editForm.value.mode,
      (newMode) => {
        // 清空其他模式相关字段
        if (newMode !== 'link') {
          editForm.value.link_url = '';
        }
        if (newMode !== 'rich') {
          editForm.value.content = '';
          // 重置编辑器内容
          if (editEditorInstance.value) {
            editEditorInstance.value.setHtml('');
          }
        }
        if (newMode !== 'exchange_product' && newMode !== 'appointment_product') {
          editForm.value.product_id = '';
        }
        // 重置表单验证状态
        if (editFormRef.value) {
          editFormRef.value.clearValidate();
        }
      }
    );
    
    // 监听添加表单商品选择变化
    watch(
      () => addForm.value.product_id,
      (newValue) => {
        // 当选择商品后，手动触发验证
        if (addFormRef.value && (addForm.value.mode === 'exchange_product' || addForm.value.mode === 'appointment_product')) {
          addFormRef.value.validateField('product_id');
        }
      }
    );
    
    // 监听编辑表单商品选择变化
    watch(
      () => editForm.value.product_id,
      (newValue) => {
        // 当选择商品后，手动触发验证
        if (editFormRef.value && (editForm.value.mode === 'exchange_product' || editForm.value.mode === 'appointment_product')) {
          editFormRef.value.validateField('product_id');
        }
      }
    );
    
    // 图片上传相关函数
    // 预览图片
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      previewVisible.value = true;
    };
    
    // 移除图片
    const handleRemove = () => {
      imageFileList.value = [];
      addForm.value.image_url = '';
    };
    
    // 图片上传前的验证
    const beforeImageUpload = (file) => {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt20M = file.size / 1024 / 1024 < 20;
      
      if (!isJPG) {
        ElMessage.error('只能上传JPG/PNG格式的图片');
      }
      if (!isLt20M) {
        ElMessage.error('图片大小不能超过20MB');
      }
      
      return isJPG && isLt20M;
    };
    
    // 图片上传成功回调
    const handleImageUploadSuccess = (response, file, fileList) => {
        // 处理后端返回的errno格式
        if (response.errno === 0) {
            // 获取相对路径，不添加完整域名
            let imgUrl = response.data[0].url;
            // 上传成功，设置轮播图图片URL（只保存相对路径）
            addForm.value.image_url = imgUrl;
            // 更新文件列表，文件列表中的预览URL需要完整地址，但保存到数据库的是相对路径
            const updatedFileList = fileList.map(item => {
                if (item.response && item.response.errno === 0 && item.response.data && item.response.data.length > 0) {
                    let fileUrl = item.response.data[0].url;
                    let previewUrl = fileUrl;
                    // 预览需要完整URL，使用API域名
                    if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                        // 从API基础URL中提取域名
                        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                        const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
                        previewUrl = `${apiDomain}${previewUrl}`;
                    }
                    return {
                        ...item,
                        url: previewUrl // 预览使用完整URL
                    };
                }
                return item;
            });
            // 保存更新后的文件列表到响应式变量
            imageFileList.value = [...updatedFileList];
            ElMessage.success('图片上传成功');
        } else {
            ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
        }
    };
    
    // 图片上传失败回调
    // eslint-disable-next-line no-unused-vars
    const handleImageUploadError = (error, file, fileList) => {
      // 尝试从error对象中获取错误信息，处理各种可能的error类型
      let errorMessage = '网络错误';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.response && error.response.data) {
          // 处理axios错误对象
          errorMessage = error.response.data.message || '未知错误';
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.statusText) {
          errorMessage = error.statusText;
        }
      }
      ElMessage.error('图片上传失败: ' + errorMessage);
    };
    
    // 编辑轮播图图片上传相关函数
    // 预览图片
    const handleEditPictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      previewVisible.value = true;
    };
    
    // 移除图片
    const handleEditRemove = () => {
      editImageFileList.value = [];
      editForm.value.image_url = '';
    };
    
    // 图片上传成功回调
    const handleEditImageUploadSuccess = (response, file, fileList) => {
        // 处理后端返回的errno格式
        if (response.errno === 0) {
            // 获取相对路径，不添加完整域名
            let imgUrl = response.data[0].url;
            // 上传成功，设置轮播图图片URL（只保存相对路径）
            editForm.value.image_url = imgUrl;
            // 更新文件列表，文件列表中的预览URL需要完整地址，但保存到数据库的是相对路径
            const updatedFileList = fileList.map(item => {
                if (item.response && item.response.errno === 0 && item.response.data && item.response.data.length > 0) {
                    let fileUrl = item.response.data[0].url;
                    let previewUrl = fileUrl;
                    // 预览需要完整URL，使用API域名
                    if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                        // 从API基础URL中提取域名
                        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                        const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
                        previewUrl = `${apiDomain}${previewUrl}`;
                    }
                    return {
                        ...item,
                        url: previewUrl // 预览使用完整URL
                    };
                }
                return item;
            });
            // 保存更新后的文件列表到响应式变量
            editImageFileList.value = [...updatedFileList];
            ElMessage.success('图片上传成功');
        } else {
            ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
        }
    };
    
    // 图片上传失败回调
    // eslint-disable-next-line no-unused-vars
    const handleEditImageUploadError = (error, file, fileList) => {
      // 尝试从error对象中获取错误信息，处理各种可能的error类型
      let errorMessage = '网络错误';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.response && error.response.data) {
          // 处理axios错误对象
          errorMessage = error.response.data.message || '未知错误';
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.statusText) {
          errorMessage = error.statusText;
        }
      }
      ElMessage.error('图片上传失败: ' + errorMessage);
    };
    
    // 提交添加轮播图表单
    const handleSubmitAddForm = async () => {
      if (!addFormRef.value) return;
      
      await addFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            await request({
              url: '/manage/banners',
              method: 'post',
              data: addForm.value
            });
            ElMessage.success('轮播图创建成功');
            addDialogVisible.value = false;
            fetchBanners();
          } catch (error) {
            ElMessage.error('轮播图创建失败');
            console.error(error);
          }
        } else {
          return false;
        }
      });
    };
    
    // 提交编辑轮播图表单
    const handleSubmitEditForm = async () => {
      if (!editFormRef.value) return;
      
      await editFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            await request({
              url: `/manage/banners/${editForm.value.id}`,
              method: 'put',
              data: editForm.value
            });
            ElMessage.success('轮播图更新成功');
            editDialogVisible.value = false;
            fetchBanners();
          } catch (error) {
            ElMessage.error('轮播图更新失败');
            console.error(error);
          }
        } else {
          return false;
        }
      });
    };
    
    // 打开编辑轮播图抽屉
    const handleEdit = (banner) => {
      // 获取轮播图详情
      fetchBannerDetail(banner.id);
      editDialogVisible.value = true;
    };
    
    // 获取轮播图详情
    const fetchBannerDetail = async (id) => {
      try {
        const response = await request({
          url: `/manage/banners/${id}`,
          method: 'get'
        });
        editForm.value = response.data;
      } catch (error) {
        ElMessage.error('获取轮播图详情失败');
        console.error(error);
      }
    };
    
    // 删除轮播图
    const handleDelete = (banner) => {
      ElMessageBox.confirm('确定要删除该轮播图吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request({
            url: `/manage/banners/${banner.id}`,
            method: 'delete'
          });
          ElMessage.success('删除成功');
          fetchBanners();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 切换轮播图状态
    const handleStatusChange = async (banner) => {
      try {
        await request({
          url: `/manage/banners/${banner.id}`,
          method: 'put',
          data: {
            status: banner.status
          }
        });
        ElMessage.success('状态更新成功');
      } catch (error) {
        ElMessage.error('状态更新失败');
        // 恢复原状态
        banner.status = banner.status === 'active' ? 'inactive' : 'active';
        console.error(error);
      }
    };
    
    // 处理轮播图排序变更
    const handleSortChange = async (banner) => {
      try {
        await request({
          url: `/manage/banners/${banner.id}`,
          method: 'put',
          data: {
            sort_order: banner.sort_order
          }
        });
        ElMessage.success('排序更新成功');
      } catch (error) {
        ElMessage.error('排序更新失败');
        console.error(error);
        // 恢复原排序值
        fetchBanners();
      }
    };
    
    // 获取模式类型，用于标签样式
    const getModeType = (mode) => {
        const typeMap = {
            display: 'info',
            link: 'success',
            exchange_product: 'primary',
            appointment_product: 'success',
            rich: 'warning'
        };
        return typeMap[mode] || 'info';
    };
    
    // 获取模式标签文本
    const getModeLabel = (mode) => {
        const labelMap = {
            display: '纯展示',
            link: '链接',
            exchange_product: '兑换商品',
            appointment_product: '预约商品',
            rich: '富文本'
        };
        return labelMap[mode] || '未知';
    };
    
    // 获取完整的图片URL
    const getFullImageUrl = (imageUrl) => {
        if (!imageUrl) return '';
        // 如果已经是完整URL，直接返回
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            return imageUrl;
        }
        // 从API基础URL中提取域名
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
        // 拼接API域名
        return `${apiDomain}${imageUrl}`;
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchBanners();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchBanners();
    };
    
    return {
      apiBaseUrl,
      banners,
      searchQuery,
      statusFilter,
      currentPage,
      pageSize,
      total,
      loading,
      exchangeProducts,
      exchangeProductsLoading,
      appointmentProducts,
      appointmentProductsLoading,
      addDialogVisible,
      addFormRef,
      addForm,
      addRules,
      imageFileList,
      previewVisible,
      dialogImageUrl,
      authToken,
      editDialogVisible,
      editFormRef,
      editForm,
      editRules,
      editImageFileList,
      editorRef,
      editorConfig,
      editorInstance,
      editEditorRef,
      editEditorConfig,
      editEditorInstance,
      fetchExchangeProducts,
      fetchAppointmentProducts,
      handleCreate,
      resetAddForm,
      handleSubmitAddForm,
      handleEdit,
      resetEditForm,
      handleSubmitEditForm,
      handleDelete,
      handleStatusChange,
      handleSortChange,
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      handlePictureCardPreview,
      handleRemove,
      beforeImageUpload,
      handleImageUploadSuccess,
      handleImageUploadError,
      handleEditPictureCardPreview,
      handleEditRemove,
      handleEditImageUploadSuccess,
      handleEditImageUploadError,
      handleCreated,
      handleChange,
      handleEditCreated,
      handleEditChange,
      getModeType,
      getModeLabel,
      getFullImageUrl
    };
  }
};
</script>

<style scoped>
.banner-list-container {
  padding: 20px;
}

.banner-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 抽屉底部按钮区域样式 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 抽屉中的表单项样式 */
.el-drawer__body .el-form-item {
  margin-bottom: 20px;
}

/* 状态容器样式 */
.status-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* wangEditor样式 */
.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.w-e-text-container {
  max-height: 300px;
  overflow-y: auto;
}
</style>